import { RightioWBSAPIModelsUdtUdtServiceAgreement } from "@src/utilities/apis/wbs/model";

interface IServiceAgreementBody
  extends Omit<
    RightioWBSAPIModelsUdtUdtServiceAgreement,
    | "dateGasSafetyDue"
    | "dateBoilerInstalled"
    | "dateBoilerLastServiced"
    | "dateStart"
    | "dateFirstPayment"
    | "dateTimeCancelled"
  > {
  dateGasSafetyDue?: Date;
  dateBoilerInstalled?: Date;
  dateBoilerLastServiced?: Date;
  dateStart?: Date;
  dateFirstPayment?: Date;
  dateTimeCancelled?: Date;
}

export class ServiceAgreementBody implements IServiceAgreementBody {
  constructor() {
    this.#serviceAgreementId = 0;
    this.#serviceAgreementReference = "";
    this.#jobId = -1;
    this.#serviceAgreementCustomerId = -1;
    this.#customerAddressId = -1;
    this.#customerPolicyAddressId = -1;
    this.#customerBillingAddressId = -1;
    this.#serviceAgreementTypeId = 0;
    this.#serviceAgreementIncentiveId = -1;
    this.#serviceAgreementStatusId = -1;
    this.#feeOneOff = 0;
    this.#feeAnnual = 0;
    this.#feeReoccurring = 0;
    this.#dateGasSafetyDue = undefined;
    this.#boilerManufacturerId = 0;
    this.#boilerModel = "";
    this.#boilerHeatingTypeId = 0;
    this.#dateBoilerInstalled = undefined;
    this.#dateBoilerLastServiced = undefined;
    this.#exclusionDetails = "";
    this.#dateStart = undefined;
    this.#dateFirstPayment = undefined;
    this.#dateTimeCancelled = undefined;
    this.#serviceAgreementCancelReasonId = 0;
    this.#referrerCode = "";
    this.#notes = "";
  }

  #serviceAgreementId: number;
  #serviceAgreementReference: string;
  #jobId: number;
  #serviceAgreementCustomerId: number;
  #customerAddressId: number;
  #customerPolicyAddressId: number;
  #customerBillingAddressId: number;
  #serviceAgreementTypeId: number;
  #serviceAgreementIncentiveId: number;
  #serviceAgreementStatusId: number;
  #feeOneOff: number;
  #feeAnnual: number;
  #feeReoccurring: number;
  #dateGasSafetyDue?: Date;
  #boilerManufacturerId: number;
  #boilerModel: string;
  #boilerHeatingTypeId: number;
  #dateBoilerInstalled?: Date;
  #dateBoilerLastServiced?: Date;
  #exclusionDetails: string;
  #dateStart?: Date;
  #dateFirstPayment?: Date;
  #dateTimeCancelled?: Date;
  #serviceAgreementCancelReasonId: number;
  #referrerCode: string;
  #notes: string;

  toObject(): any {
    return {
      _serviceAgreementId: this.#serviceAgreementId,
      _serviceAgreementReference: this.#serviceAgreementReference,
      _jobId: this.#jobId,
      _serviceAgreementCustomerId: this.#serviceAgreementCustomerId,
      _customerAddressId: this.#customerAddressId,
      _customerPolicyAddressId: this.#customerPolicyAddressId,
      _customerBillingAddressId: this.#customerBillingAddressId,
      _serviceAgreementTypeId: this.#serviceAgreementTypeId,
      _serviceAgreementIncentiveId: this.#serviceAgreementIncentiveId,
      _serviceAgreementStatusId: this.#serviceAgreementStatusId,
      _feeOneOff: this.#feeOneOff,
      _feeAnnual: this.#feeAnnual,
      _feeReoccurring: this.#feeReoccurring,
      _dateGasSafetyDue: this.#dateGasSafetyDue,
      _boilerManufacturerId: this.#boilerManufacturerId,
      _boilerModel: this.#boilerModel,
      _boilerHeatingTypeId: this.#boilerHeatingTypeId,
      _dateBoilerInstalled: this.#dateBoilerInstalled,
      _dateBoilerLastServiced: this.#dateBoilerLastServiced,
      _exclusionDetails: this.#exclusionDetails,
      _dateStart: this.#dateStart,
      _dateFirstPayment: this.#dateFirstPayment,
      _dateTimeCancelled: this.#dateTimeCancelled,
      _serviceAgreementCancelReasonId: this.#serviceAgreementCancelReasonId,
      _referrerCode: this.#referrerCode,
      _notes: this.#notes,
    };
  }

  /**
   * Getter serviceAgreementId
   * @return {number}
   */
  public get serviceAgreementId(): number {
    return this.#serviceAgreementId;
  }

  /**
   * Getter serviceAgreementReference
   * @return {string}
   */
  public get serviceAgreementReference(): string {
    return this.#serviceAgreementReference;
  }

  /**
   * Getter jobId
   * @return {number}
   */
  public get jobId(): number {
    return this.#jobId;
  }

  /**
   * Getter serviceAgreementCustomerId
   * @return {number}
   */
  public get serviceAgreementCustomerId(): number {
    return this.#serviceAgreementCustomerId;
  }

  /**
   * Getter customerAddressId
   * @return {number}
   */
  public get customerAddressId(): number {
    return this.#customerAddressId;
  }

  /**
   * Getter customerPolicyAddressId
   * @return {number}
   */
  public get customerPolicyAddressId(): number {
    return this.#customerPolicyAddressId;
  }

  /**
   * Getter customerBillingAddressId
   * @return {number}
   */
  public get customerBillingAddressId(): number {
    return this.#customerBillingAddressId;
  }

  /**
   * Getter serviceAgreementTypeId
   * @return {number}
   */
  public get serviceAgreementTypeId(): number {
    return this.#serviceAgreementTypeId;
  }

  /**
   * Getter serviceAgreementIncentiveId
   * @return {number}
   */
  public get serviceAgreementIncentiveId(): number {
    return this.#serviceAgreementIncentiveId;
  }

  /**
   * Getter serviceAgreementStatusId
   * @return {number}
   */
  public get serviceAgreementStatusId(): number {
    return this.#serviceAgreementStatusId;
  }

  /**
   * Getter feeOneOff
   * @return {number}
   */
  public get feeOneOff(): number {
    return this.#feeOneOff;
  }

  /**
   * Getter feeAnnual
   * @return {number}
   */
  public get feeAnnual(): number {
    return this.#feeAnnual;
  }

  /**
   * Getter feeReoccurring
   * @return {number}
   */
  public get feeReoccurring(): number {
    return this.#feeReoccurring;
  }

  /**
   * Getter dateGasSafetyDue
   * @return {Date | undefined}
   */
  public get dateGasSafetyDue(): Date | undefined {
    return this.#dateGasSafetyDue;
  }

  /**
   * Getter boilerManufacturerId
   * @return {number}
   */
  public get boilerManufacturerId(): number {
    return this.#boilerManufacturerId;
  }

  /**
   * Getter boilerModel
   * @return {string}
   */
  public get boilerModel(): string {
    return this.#boilerModel;
  }

  /**
   * Getter boilerHeatingTypeId
   * @return {number}
   */
  public get boilerHeatingTypeId(): number {
    return this.#boilerHeatingTypeId;
  }

  /**
   * Getter dateBoilerInstalled
   * @return {Date | undefined}
   */
  public get dateBoilerInstalled(): Date | undefined {
    return this.#dateBoilerInstalled;
  }

  /**
   * Getter dateBoilerLastServiced
   * @return {Date | undefined}
   */
  public get dateBoilerLastServiced(): Date | undefined {
    return this.#dateBoilerLastServiced;
  }

  /**
   * Getter exclusionDetails
   * @return {string}
   */
  public get exclusionDetails(): string {
    return this.#exclusionDetails;
  }

  /**
   * Getter dateStart
   * @return {Date | undefined}
   */
  public get dateStart(): Date | undefined {
    return this.#dateStart;
  }

  /**
   * Getter dateFirstPayment
   * @return {Date | undefined}
   */
  public get dateFirstPayment(): Date | undefined {
    return this.#dateFirstPayment;
  }

  /**
   * Getter dateTimeCancelled
   * @return {Date | undefined}
   */
  public get dateTimeCancelled(): Date | undefined {
    return this.#dateTimeCancelled;
  }

  /**
   * Getter serviceAgreementCancelReasonId
   * @return {number}
   */
  public get serviceAgreementCancelReasonId(): number {
    return this.#serviceAgreementCancelReasonId;
  }

  /**
   * Getter referrerCode
   * @return {string}
   */
  public get referrerCode(): string {
    return this.#referrerCode;
  }

  /**
   * Getter notes
   * @return {string}
   */
  public get notes(): string {
    return this.#notes;
  }

  /**
   * Setter serviceAgreementId
   * @param {number} value
   */
  public set serviceAgreementId(value: number) {
    this.#serviceAgreementId = value;
  }

  /**
   * Setter serviceAgreementReference
   * @param {string} value
   */
  public set serviceAgreementReference(value: string) {
    this.#serviceAgreementReference = value;
  }

  /**
   * Setter jobId
   * @param {number} value
   */
  public set jobId(value: number) {
    this.#jobId = value;
  }

  /**
   * Setter serviceAgreementCustomerId
   * @param {number} value
   */
  public set serviceAgreementCustomerId(value: number) {
    this.#serviceAgreementCustomerId = value;
  }

  /**
   * Setter customerAddressId
   * @param {number} value
   */
  public set customerAddressId(value: number) {
    this.#customerAddressId = value;
  }

  /**
   * Setter customerPolicyAddressId
   * @param {number} value
   */
  public set customerPolicyAddressId(value: number) {
    this.#customerPolicyAddressId = value;
  }

  /**
   * Setter customerBillingAddressId
   * @param {number} value
   */
  public set customerBillingAddressId(value: number) {
    this.#customerBillingAddressId = value;
  }

  /**
   * Setter serviceAgreementTypeId
   * @param {number} value
   */
  public set serviceAgreementTypeId(value: number) {
    this.#serviceAgreementTypeId = value;
  }

  /**
   * Setter serviceAgreementIncentiveId
   * @param {number} value
   */
  public set serviceAgreementIncentiveId(value: number) {
    this.#serviceAgreementIncentiveId = value;
  }

  /**
   * Setter serviceAgreementStatusId
   * @param {number} value
   */
  public set serviceAgreementStatusId(value: number) {
    this.#serviceAgreementStatusId = value;
  }

  /**
   * Setter feeOneOff
   * @param {number} value
   */
  public set feeOneOff(value: number) {
    this.#feeOneOff = value;
  }

  /**
   * Setter feeAnnual
   * @param {number} value
   */
  public set feeAnnual(value: number) {
    this.#feeAnnual = value;
  }

  /**
   * Setter feeReoccurring
   * @param {number} value
   */
  public set feeReoccurring(value: number) {
    this.#feeReoccurring = value;
  }

  /**
   * Setter dateGasSafetyDue
   * @param {Date|null } value
   */
  public set dateGasSafetyDue(value: Date | undefined) {
    this.#dateGasSafetyDue = value;
  }

  /**
   * Setter boilerManufacturerId
   * @param {number} value
   */
  public set boilerManufacturerId(value: number) {
    this.#boilerManufacturerId = value;
  }

  /**
   * Setter boilerModel
   * @param {string} value
   */
  public set boilerModel(value: string) {
    this.#boilerModel = value;
  }

  /**
   * Setter boilerHeatingTypeId
   * @param {number} value
   */
  public set boilerHeatingTypeId(value: number) {
    this.#boilerHeatingTypeId = value;
  }

  /**
   * Setter dateBoilerInstalled
   * @param {Date|null } value
   */
  public set dateBoilerInstalled(value: Date | undefined) {
    this.#dateBoilerInstalled = value;
  }

  /**
   * Setter dateBoilerLastServiced
   * @param {Date|null } value
   */
  public set dateBoilerLastServiced(value: Date | undefined) {
    this.#dateBoilerLastServiced = value;
  }

  /**
   * Setter exclusionDetails
   * @param {string} value
   */
  public set exclusionDetails(value: string) {
    this.#exclusionDetails = value;
  }

  /**
   * Setter dateStart
   * @param {Date|null } value
   */
  public set dateStart(value: Date | undefined) {
    this.#dateStart = value;
  }

  /**
   * Setter dateFirstPayment
   * @param {Date|null } value
   */
  public set dateFirstPayment(value: Date | undefined) {
    this.#dateFirstPayment = value;
  }

  /**
   * Setter dateTimeCancelled
   * @param {Date|null } value
   */
  public set dateTimeCancelled(value: Date | undefined) {
    this.#dateTimeCancelled = value;
  }

  /**
   * Setter serviceAgreementCancelReasonId
   * @param {number} value
   */
  public set serviceAgreementCancelReasonId(value: number) {
    this.#serviceAgreementCancelReasonId = value;
  }

  /**
   * Setter referrerCode
   * @param {string} value
   */
  public set referrerCode(value: string) {
    this.#referrerCode = value;
  }

  /**
   * Setter notes
   * @param {string} value
   */
  public set notes(value: string) {
    this.#notes = value;
  }
}
